import React, { CSSProperties } from "react";

/**
 * En dekoration i bakgrunden. Placeras med procent av viewporten så att
 * scenerna skalar med skärmen. Klasserna `hide-sm`/`hide-md` döljer
 * dekorationer som skulle krocka med innehållet på smala skärmar.
 *
 * Tre lager:
 *  .hol-prop        position
 *  .hol-prop-pop    entré-animation när temat laddas
 *  .hol-prop-inner  rotation/spegling + den löpande animationen
 */
export interface PropProps {
  /** Vänsterkant i % av viewportbredden. */
  left?: string;
  right?: string;
  /** Avstånd från botten (vh eller %). */
  bottom?: string;
  top?: string;
  /** CSS-bredd, gärna clamp(). */
  width: string;
  /** Rotation i grader. */
  rotate?: number;
  flip?: boolean;
  opacity?: number;
  /** Namn på CSS-animation (utan hol- prefix) och dess parametrar. */
  anim?: string;
  duration?: string;
  delay?: string;
  z?: number;
  /** Äldre parallaxdjup. Bakgrunden står numera still; värdet ignoreras. */
  depth?: number;
  className?: string;
  hideBelow?: "sm" | "md" | "lg";
  style?: CSSProperties;
  children: React.ReactNode;
}

const parsePercent = (value?: string): number | null => {
  if (!value) return null;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

const Prop: React.FC<PropProps> = ({
  left,
  right,
  bottom,
  top,
  width,
  rotate = 0,
  flip = false,
  opacity,
  anim,
  duration,
  delay,
  z,
  className = "",
  hideBelow,
  style,
  children,
}) => {
  const transform = [
    rotate ? `rotate(${rotate}deg)` : "",
    flip ? "scaleX(-1)" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Entrén sveper från vänster till höger över skärmen.
  const leftPct = parsePercent(left);
  const rightPct = parsePercent(right);
  const xPct = leftPct ?? (rightPct !== null ? 100 - rightPct : 50);
  const popDelay = `${Math.round(120 + (xPct / 100) * 520)}ms`;

  const outer: CSSProperties & Record<string, string | number | undefined> = {
    left,
    right,
    bottom,
    top,
    width,
    opacity,
    zIndex: z,
    ...style,
  };

  const inner: CSSProperties = {
    transform: transform || undefined,
    animationName: anim ? `hol-${anim}` : undefined,
    animationDuration: duration,
    animationDelay: delay,
  };

  const hideClass = hideBelow ? `hide-${hideBelow}` : "";

  return (
    <div className={`hol-prop ${hideClass} ${className}`.trim()} style={outer}>
      <div className="hol-prop-pop" style={{ animationDelay: popDelay }}>
        <div className={`hol-prop-inner ${anim ? "hol-animated" : ""}`} style={inner}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Prop;
